<script setup lang="ts" generic="T">
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import { useId } from 'vue'

type CardOption = {
    label: string
    description?: string
    icon?: string
    iconText?: string
    image?: string
    value: T
    disabled?: boolean
}

const props = defineProps<{
    options: CardOption[]
    colsClass?: string
    disabled?: boolean
    single?: boolean
    dense?: boolean
    ariaDescribedby?: string
}>()

const values = defineModel<T[]>({ default: () => [] })
const singleValue = defineModel<T>('singleValue')

const toggleValue = (optionValue: T, val: boolean) => {
    const current = values.value ?? []
    values.value = val
        ? current.includes(optionValue)
            ? current
            : [...current, optionValue]
        : current.filter((v) => v !== optionValue)
}

const uid = useId()

const cardLabelClasses = (option: CardOption): string[] => {
    return [getEnabledOrDisabledClasses(option), getDenseClass()]
}

const getEnabledOrDisabledClasses = (option: CardOption): string => {
    return option.disabled || props.disabled
        ? getDisabledClasses()
        : getSingleOrMultiClasses(option)
}

const getDisabledClasses = (): string => {
    return 'cursor-not-allowed opacity-50 bg-surface-100 border-surface-200 text-neutral-600'
}

const getSingleOrMultiClasses = (option: CardOption): string => {
    return (
        props.single ? singleValue.value === option.value : values.value?.includes(option.value)
    )
        ? 'cursor-pointer border-primary-500 text-primary-600 bg-primary-100'
        : 'cursor-pointer bg-white border-surface-300 text-black hover:text-primary-600 hover:border-primary-500 hover:bg-primary-50'
}

const getDenseClass = (): string => {
    return props.dense ? 'p-3' : 'p-6'
}
</script>
<template>
    <div :class="`grid ${colsClass || 'md:grid-cols-2'} gap-4`">
        <label
            v-for="(option, index) of options"
            :key="`${option.value}-${index}`"
            class="group relative min-w-0 border rounded-2xl flex flex-col gap-3 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 transition-colors"
            :class="cardLabelClasses(option)"
            :for="uid + option.value"
        >
            <span v-if="option.icon" class="size-8 text-black flex items-center justify-center">
                <i :class="`${option.icon} text-4xl`" aria-hidden="true" />
            </span>
            <span v-if="option.iconText" class="size-8 flex items-center justify-center">
                {{ option.iconText }}
            </span>
            <span v-if="option.image" class="w-12 flex items-center justify-center">
                <img :src="option.image" alt="" loading="lazy" decoding="async" class="w-11 h-8" />
            </span>
            <div>
                <p class="font-medium mb-1 wrap-anywhere">{{ $t(option.label) }}</p>
                <p
                    v-if="option.description"
                    class="paragraph-sm whitespace-pre-line wrap-anywhere group-hover:text-current"
                    :class="
                        (single ? singleValue === option.value : values?.includes(option.value))
                            ? 'text-primary-600'
                            : 'text-neutral-600'
                    "
                >
                    {{ $t(option.description) }}
                </p>
            </div>
            <RadioButton
                v-if="single"
                v-model="singleValue"
                :input-id="uid + option.value"
                :value="option.value"
                class="sr-only"
                :disabled="disabled || option.disabled"
                :aria-describedby="ariaDescribedby"
            />
            <Checkbox
                v-else
                binary
                :input-id="uid + option.value"
                :model-value="values?.includes(option.value)"
                class="sr-only"
                :disabled="disabled || option.disabled"
                :aria-describedby="ariaDescribedby"
                @update:model-value="(val) => toggleValue(option.value, val)"
            />
        </label>
    </div>
</template>
